<template>
  <header class="header bg__color--white">
    <div class="container header__logo d-flex align-items-center justify-content-between">
      <app-logo
        class="logo"
        :logo="logo.img"
        :url="logo.url"
      />

      <div class="d-flex align-items-center">
        <app-session-button
          class="session link__color--sec d-flex align-items-center"
          @click="show_mobile_menu = false"
          @close="show_mobile_menu = false"
        />

        <language-switcher v-slot="{ options, updateLanguage, isActive }">
          <ul class="language__ul">
            <li
              class="language__li"
              v-for="language in options"
              :key="language.id"
            >
              <button
                type="button"
                class="link--naked language__button"
                :class="{ 'is-active': isActive(language.id) }"
                @click.prevent="updateLanguage(language.id)"
              >
                {{ language.label.value.toUpperCase() }}
              </button>
            </li>
          </ul>
        </language-switcher>
      </div>
    </div>

    <div class="container bg__color--vdw home header__wrapper d-flex align-items-center justify-content-between">
      <app-link
        :link="homepage"
        class="link__color--sec link--naked d-flex align-items-center bold home__link"
        v-slot="{ text }"
      >
        <img
          :src="translate(home.icon)"
          alt="Home icon"
          class="home__icon"
          height="22"
          aria-hidden="true"
        >

        {{ text }}
      </app-link>

      <app-burger-menu
        class="header__burger d-md-none"
        :state="show_mobile_menu"
        @toggle="toggleMobileMenu"
      />
    </div>

    <nav
      class="container bg__color--vdw header__wrapper d-none d-md-flex align-items-center justify-content-between navigation"
      :class="{ 'd-block': show_mobile_menu }"
    >
      <ul
        class="navigation__ul d-lg-flex align--center"
        v-if="navigation.links.length"
      >
        <li
          v-for="({ link }, index) in navigation.links"
          :key="`navigation_${index}`"
          class="navigation__li"
        >
          <app-link
            :link="link"
            class="navigation__a link--naked link__color--sec"
          />
        </li>
      </ul>

      <ul
        class="actions d-lg-flex align--center"
        v-if="navigation.call_to_actions.length"
      >
        <template
          v-for="({ link, color }, index) in navigation.call_to_actions"
          :key="`action_${index}`"
        >
          <li
            :key="`action_${index}`"
            class="actions__li"
            v-if="link?.show"
          >
            <app-link
              :link="link"
              :class="`actions__a btn btn__size--small btn__color--${color} d-block`"
            />
          </li>
        </template>
      </ul>
    </nav>
  </header>
</template>

<script setup>
import { ref, watch, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '@/vendors/i18n'
import { scrollTo } from '@/utils/utils'
import Link from '@/classes/links/Link'

import AppLogo from '&/atoms/AppLogo'
import AppSessionButton from '&/organisms/AppSessionButton'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppBurgerMenu = defineAsyncComponent(() => import('&/atoms/AppBurgerMenu'))
const LanguageSwitcher = defineAsyncComponent(() => import('&/atoms/LanguageSwitcher'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const route = useRoute()
const { translate } = useI18n()

const show_mobile_menu = ref(false)

const { logo = {}, home = {}, navigation = {} } = section.attributes

const homepage = new Link({ ...home, target_url: '/' })

const toggleMobileMenu = () => {
  scrollTo()

  setTimeout(() => show_mobile_menu.value = !show_mobile_menu.value, 100)
}

watch(route, () => show_mobile_menu.value = false)
</script>


<style scoped lang="scss">
.header {
  &__logo {
    padding: 8px 16px;
    gap: 12px;

    @include mq(md) {
      padding: 8px 0;
    }

    & > div:first-child {
      flex-shrink: 1;
      display: flex;
      align-items: center;
    }
  }

  &__wrapper {
    padding-inline: 16px;
  }
}

:deep() {
  .logo {
    width: 100%;
    height: auto;
    max-height: 26px;
  }

  .session {
    margin-right: 6px;
    font-size: rem(15px);
    flex: 0 0 auto;

    @include mq(sm) {
      margin-right: 16px;
    }

    &__text {
      display: none;

      @include mq(sm) {
        display: block;
      }
    }
  }

  .locale-switcher {
    background: none;
    display: block;
  }

  .menu.menu {
    padding: 6px 8px;
    font-size: rem(13px);
    text-transform: uppercase;
    flex-direction: column-reverse;
    text-align: left;
    font-weight: 700;
    color: $color--secondary;

    .menu {
      &__label {
        margin: 3px 0 0;
      }

      &__line {
        background: $color--secondary;

        &:nth-child(3) {
          width: 24px;
        }
      }

      &__close {
        &::before,
        &::after {
          background: $color--secondary;
        }
      }
    }
  }
}

.home {
  font-size: rem(22px);
  font-weight: 500;
  text-transform: uppercase;
  padding-block: 12px;

  @include mq(md) {
    padding-block: 22px;
  }

  &__icon {
    margin-right: 6px;
  }

  :deep() {
    .home__link {
      line-height: 1.27;
    }
  }
}

.navigation {
  font-size: rem(14px);
  border-bottom: 2px solid $white;
  padding-block: 8px;

  @include mq(md) {
    font-size: rem(18px);
    padding-left: 0;
    padding-bottom: 0;
  }

  @include mq(lg) {
    font-size: rem(22px);
  }

  &__ul {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 8px;

    @include mq(md) {
      position: relative;
      bottom: -2px;
      display: flex;
      flex: 1 1 auto;
      gap: 0;
      margin: 0 12px 0 0;
      overflow-y: auto;
      scrollbar-color: $color--secondary transparent;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        width: 2px;
      }
    }
  }

  :deep() {
    .navigation {
      &__a {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #e6e6e6;
        border-right: 4px;
        padding: 8px;
        min-height: 100%;
        line-height: 1.45;

        &.is-active {
          background-color: $color--secondary;
          color: #fff;
        }

        @include mq(md) {
          background: none;
          padding: 8px 16px 10px;
          white-space: nowrap;
          display: block;
          font-weight: 700;

          &:focus,
          &:hover{
            background-color: #e6e6e6;
          }

          &.is-active {
            background: none;
            color: $color--secondary;

            &:hover {
              background-color: #e6e6e6;
            }

            &::after {
              content: "";
              display: block;
              width: 100%;
              height: 2px;
              background: $color--secondary;
              position: absolute;
              left: 0;
              bottom: 0;
            }
          }
        }
      }
    }
  }
}

.language {
  &__ul {
    display: flex;
    align-items: center;
  }

  &__li {
    &:not(:last-child) {
      border-right: 1px solid $color--secondary;
    }

    &:first-child {
      .language__button {
        padding-left: 0;
      }
    }

    &:last-child {
      .language__button {
        padding-right: 0;
      }
    }
  }

  &__button {
    padding-inline: 6px;
    color: #6d6a6a;
    transition: color .3s linear;
    font-size: rem(15px);
    line-height: 1.2;
    display: block;

    @include mq(sm) {
      padding-inline: 8px;
    }

    &:hover,
    &.is-active {
      color: $color--secondary;
    }
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 0 auto;
  font-size: rem(16px);

  @include mq(lg) {
    font-size: rem(22px);
  }

  @include mq(md) {
    flex-wrap: nowrap;
  }

  &__li {
    flex: 1 1 150px;

    @include mq(md) {
      flex: 0 0 auto;
    }
  }

  :deep() {
    .actions {
      &__button {
        white-space: normal;
        word-break: break-word;
      }
    }
  }
}
</style>